import { isValid as isValidPublicDomain } from 'psl';
import type { ListingResponse } from '../services/listing_service';
import { AI_TECHNOLOGY_EXTENSION_MAP } from './aiListingExtensionTypes';

const EXTERNAL_PROVISION_FLOW = 'External';

type ExternalInstallData = {
    provisionFlow?: unknown;
    installUrl?: unknown;
};

function isIpAddress(hostname: string): boolean {
    const unwrappedHostname = hostname.startsWith('[') && hostname.endsWith(']') ? hostname.slice(1, -1) : hostname;
    if (unwrappedHostname.includes(':')) {
        return true;
    }

    const octets = unwrappedHostname.split('.');
    return (
        octets.length === 4 &&
        octets.every((octet) => /^\d+$/.test(octet) && Number(octet) >= 0 && Number(octet) <= 255)
    );
}

function isPublicHostname(hostname: string): boolean {
    const normalizedHostname = hostname.toLowerCase().replace(/\.+$/, '');
    return normalizedHostname.length > 0 && isValidPublicDomain(normalizedHostname);
}

export interface ExternalInstallConfig {
    isExternal: boolean;
    installUrl: string;
    installUrlHost?: string;
}

function getExternalInstallData(listing?: ListingResponse | null): ExternalInstallData | null {
    const technology = listing?.technology;
    if (technology !== 'ai-skill' && technology !== 'ai-plugin') {
        return null;
    }

    const extensionType = AI_TECHNOLOGY_EXTENSION_MAP[technology];
    const extension = listing?.extensions?.find((candidate) => candidate.extensionType === extensionType);
    return (extension?.data as ExternalInstallData | undefined) ?? null;
}

/**
 * Reads the external-install contract shared by Skill and Plugin extensions.
 *
 * `provisionFlow` is intentionally optional for backward compatibility: only the explicit
 * `External` value opts a listing out of the native install experience. The install URL is
 * accepted only when it is an absolute public-host HTTPS URL without embedded credentials, since
 * it is bound directly to an outbound CTA. Public-suffix validation rejects IP literals,
 * single-label hosts, and local/special-use namespaces while permitting multiple public marketplaces.
 * The external mode remains true when the URL is absent or invalid so malformed upstream data
 * fails closed instead of exposing the native installer for a listing that cannot use it.
 */
export function getExternalInstallConfig(listing?: ListingResponse | null): ExternalInstallConfig {
    const data = getExternalInstallData(listing);
    if (data?.provisionFlow !== EXTERNAL_PROVISION_FLOW) {
        return { isExternal: false, installUrl: '' };
    }

    if (typeof data.installUrl !== 'string') {
        return { isExternal: true, installUrl: '' };
    }

    const installUrl = data.installUrl.trim();
    try {
        const parsed = new URL(installUrl);
        if (
            parsed.protocol !== 'https:' ||
            parsed.username ||
            parsed.password ||
            isIpAddress(parsed.hostname) ||
            !isPublicHostname(parsed.hostname)
        ) {
            return { isExternal: true, installUrl: '' };
        }
        // Return the URL parser's canonical absolute form. Inputs such as
        // `https:marketplace.example.com/install` are valid absolute URLs, but binding the original
        // string to an anchor would make the browser resolve it against the current page.
        return { isExternal: true, installUrl: parsed.href, installUrlHost: parsed.hostname };
    } catch {
        return { isExternal: true, installUrl: '' };
    }
}
